/**
 * Espaces encadrants — « Deux casquettes » (Lot 0).
 *
 * Un encadrant a deux casquettes : Opérationnel (il s'entraîne, comme tout le monde)
 * et Encadrant (il pilote, anime, conçoit). Le toggle en tête bascule toute la nav.
 * En mode Encadrant, la barre = les BLOCS du rôle (Piloter · Animer · Concevoir ·
 * Administrer), colorés par rôle.
 *
 * Périmètre ACTIF : Manager + Organisateur.
 *
 * Le CSL avait été ouvert le 25/07 comme organisateur multi-comptes — mêmes
 * écrans, une maille au-dessus, périmètre borné à son portefeuille
 * (`user_entity_access`) et jamais à toute la plateforme, cf. `getVisibleUserIds`.
 * L'ouverture est REPORTÉE : elle attend l'arbitrage des droits et, surtout, le
 * provisionnement de `user_entity_access`. Tout le nécessaire reste en place et
 * inactif ; rouvrir tient en une ligne dans `encadrantRoleFor`.
 */

import { canCollaborator } from "./collaborator-permissions";

export type EncadrantRole = "mng" | "org" | "csl";
export type EncadrantBloc = "piloter" | "animer" | "concevoir" | "administrer" | "portefeuille" | "transverse";

/** Rôle encadrant d'un profil, ou null s'il n'a pas de casquette encadrant (BNF, GES, RH, CSL). */
export function encadrantRoleFor(profileType: string): EncadrantRole | null {
  if (profileType === "MNG") return "mng";
  // CSL fermé — décision de Valérie (25/09/2026, arbitrage PR #7) : pas d'ouverture
  // tant que ses droits ne sont pas arbitrés dans un chantier dédié (cloisonnement strict
  // par client). La mécanique du portefeuille (`user_entity_access`, Admin › Portefeuilles,
  // `accountContextFor`) reste en place, inactive ; rouvrir tient en une ligne ici.
  if (profileType === "CSL") return null;
  if (profileType === "ORG" || profileType === "ADM" || profileType === "COO") return "org";
  return null;
}

/**
 * Qui peut MONTER une campagne — une seule liste, lue par le serveur et par l'UI.
 *
 * Avoir la casquette encadrant suffit pour VOIR l'écran des campagnes, pas pour
 * en créer une. Tant que le front ignorait cette nuance, il affichait le tunnel
 * de création à qui n'y avait pas droit : l'utilisateur choisissait sa situation,
 * remplissait le cadrage, cliquait « créer » — et recevait un 403 que
 * `createFromPlan` n'attrapait même pas, laissant la modale ouverte sans un mot.
 *
 * Le dépôt portait déjà quatre copies divergentes de cette liste. Celle-ci fait
 * foi pour les campagnes ; `src/lib/campaign-steps.ts` documente le même piège.
 */
export const CAMPAIGN_MANAGE_PROFILES = ["ORG", "ADM", "COO", "MNG"] as const;

/** Ce profil peut-il monter une campagne ? Même réponse côté serveur et client. */
export function canManageCampaigns(profileType?: string): boolean {
  return !!profileType && (CAMPAIGN_MANAGE_PROFILES as readonly string[]).includes(profileType);
}

/**
 * Attribuer une licence DU POOL du compte à quelqu'un de son périmètre. Lu dans la
 * matrice collaborateurs (source unique, décision du 18/09/2026) : le MNG y a droit
 * pour son équipe. Attribuer n'est pas acheter — l'achat reste fermé au MNG.
 * (La branche PR #7 en portait une copie divergente limitée à ORG/ADM.)
 * Lue par `readLicenceStatus` (plan.service) : le bouton « Attribuer une licence » du choix
 * des participants n'apparaît que si elle ET la règle complète (`checkCollaboratorAction`,
 * sous-arbre MNG) l'autorisent — la même que `handleAssignLicence` applique au serveur.
 */
export function canManageLicences(profileType?: string): boolean {
  return canCollaborator(profileType, "licences");
}

/**
 * ACHETER — packs, licences, abonnement : organisateur, admin et GESTIONNAIRE FINANCIER
 * (décision Valérie, Q3 du 29-30/09/2026 ; auparavant ORG / ADM seulement). JAMAIS le RH (il gère
 * collaborateurs, licences du pool et factures, il n'engage pas la dépense), jamais le COO, le MNG
 * ni le BNF. `hasLicencesTab` (qui inclut RH / GES) ne dit PAS qui achète.
 *
 * SOURCE UNIQUE, serveur ET écran : `src/api/buy-pack.ts`, `POST /api/zoho/buy-again`
 * (`handleBuyAgain`), l'activation d'un essai (`trial-runtime.ts`), le bouton « Acheter des
 * licences » du choix des participants (`plan.service.ts`), les boutons d'achat de « Mon compte »,
 * la fenêtre de blocage d'une session, le bandeau de quota et le compteur d'en-tête. Le dépôt en
 * portait quatre copies divergentes (ORG/ADM/COO côté écran, ORG/ADM/GES côté Zoho) : le COO voyait
 * un bouton que le serveur refusait. Ne JAMAIS redéclarer une liste de profils acheteurs.
 */
export const LICENCE_BUY_PROFILES = ["ORG", "ADM", "GES"] as const;

export function canBuyLicences(profileType?: string | null): boolean {
  return !!profileType && (LICENCE_BUY_PROFILES as readonly string[]).includes(profileType);
}

/**
 * Où mène « Acheter un pack » — la MÊME destination partout (correction n°5 du 30/09/2026) :
 * « Mon compte › Licences et Packs », où vivent le rachat d'un pack et l'ajout de licences.
 * Avant, l'accueil et le compteur d'en-tête ouvraient la page `buy-pack` (neutralisée depuis
 * juin : « achat indisponible »), la fenêtre de blocage renvoyait vers `/account/buy-pack`
 * (adresse inconnue du routeur → retour à l'accueil) et Pilot vers « Mon compte ». Tous les
 * boutons lisent désormais ces constantes.
 */
export const ACHAT_PACK = { page: "account", tab: "licences" } as const;
export const ACHAT_PACK_URL = `/${ACHAT_PACK.page}?tab=${ACHAT_PACK.tab}`;
/** Réabonnement d'un compte dont l'abonnement a pris fin : même onglet, fenêtre d'achat ouverte. */
export const REABONNEMENT_URL = `${ACHAT_PACK_URL}&buy=1`;

/** Navigation de l'application (`App.handleNavigate`) vue par un bouton d'achat. */
export type NavigationVersAchat = (page: typeof ACHAT_PACK.page, target: { tab: string }) => void;

/** Ouvre la destination d'achat — l'UNIQUE fonction qu'appellent les boutons « Acheter un pack ». */
export function ouvrirAchatDePack(onNavigate: NavigationVersAchat): void {
  onNavigate(ACHAT_PACK.page, { tab: ACHAT_PACK.tab });
}

/**
 * A-t-il l'onglet « Licences et Packs » de Mon compte, où l'on ouvre une licence ?
 * Source unique pour l'écran (AccountPage) ET pour les messages du Plan : un encadrant
 * qui ne l'a pas (MNG) se voit proposer « Demander une licence », jamais « ouvrez-lui la
 * licence » — il ne saurait pas où la trouver. Distinct de `canManageLicences` (le DROIT
 * d'attribuer, matrice) tant que l'attribution depuis la campagne n'existe pas (lot 4).
 */
export function hasLicencesTab(profileType?: string): boolean {
  return ["ORG", "ADM", "RH", "GES"].includes(profileType ?? "");
}

export const ENCADRANT_ROLE_META: Record<EncadrantRole, {
  color: string; fill: string; spaceKey: string; blocs: EncadrantBloc[];
}> = {
  mng: { color: "#926B00", fill: "#FBF3DA", spaceKey: "encadrant.space.mng", blocs: ["piloter", "animer", "concevoir"] },
  org: { color: "#5A4BBE", fill: "#EDE9FB", spaceKey: "encadrant.space.org", blocs: ["piloter", "animer", "concevoir"] },
  // Portefeuille et vue transverse viendront avec leurs écrans ; d'ici là un
  // bloc sans sous-écran n'est pas proposé.
  // Consultant : il pilote et planifie AVEC le client (plan, campagnes, assignation) ;
  // les objets du client (cas, offres, personae, critères) restent au client — les API
  // le refusent déjà en écriture, on ne lui montre pas un magasin où tout finirait en 403.
  csl: { color: "#0F7B8A", fill: "#E1F1F4", spaceKey: "encadrant.space.csl", blocs: ["piloter", "animer"] },
};

export const ENCADRANT_BLOC_LABEL: Record<EncadrantBloc, string> = {
  piloter: "encadrant.bloc.piloter",
  animer: "encadrant.bloc.animer",
  concevoir: "encadrant.bloc.concevoir",
  administrer: "encadrant.bloc.administrer",
  portefeuille: "encadrant.bloc.portefeuille",
  transverse: "encadrant.bloc.transverse",
};

/**
 * Données d'ENTRAÎNEMENT de tout le compte (sessions, scores, transcriptions, exercices, reporting) :
 * ORG et RH seulement — JAMAIS le GES, dont la vocation est uniquement administrative et financière
 * (abonnements, packs, factures, gestion des utilisateurs), décision de Valérie du 29/09/2026. Source
 * unique, lue par le serveur (`/api/sessions`, `/api/reporting`, détail d'une session ou d'un
 * exercice) ET par l'écran (sélecteur de collaborateur de l'historique, page « Mon équipe »).
 */
export const ACCOUNT_TRAINING_VIEWERS = ["ORG", "RH"] as const;
export function canViewAccountTrainingData(profileType?: string | null): boolean {
  return !!profileType && (ACCOUNT_TRAINING_VIEWERS as readonly string[]).includes(profileType);
}

/**
 * Résultats d'une COMPÉTITION entre collègues (tournoi : participants, vainqueurs, matchs) : ouverts à
 * tous les profils qui s'entraînent, JAMAIS au GES sauf s'il y est lui-même inscrit (décision de
 * Valérie du 29/09/2026, B1 — même règle que le détail d'un challenge).
 */
export function canViewCompetitionResults(profileType: string | null | undefined, enrolled: boolean): boolean {
  return profileType !== "GES" || enrolled;
}

/** Page « Mon équipe » (reporting) : qui l'API `/api/reporting` sert — ADM, ORG, RH, et le MNG pour son équipe. */
export function canOpenTeamReporting(profileType?: string | null): boolean {
  return profileType === "ADM" || profileType === "MNG" || canViewAccountTrainingData(profileType);
}

/**
 * Onglet « Factures » de Mon compte : ORG (et ADM qui gère le compte), RH et GES — le GES y a sa
 * place, sa vocation étant administrative et financière (décision de Valérie du 29/09/2026 ; il
 * n'avait pas l'onglet).
 */
export const INVOICE_PROFILES = ["ORG", "ADM", "RH", "GES"] as const;
export function canSeeInvoices(profileType?: string | null): boolean {
  return !!profileType && (INVOICE_PROFILES as readonly string[]).includes(profileType);
}

/**
 * Le COO placé chez un client dans Pilot LIT la liste des collaborateurs et des cas du compte choisi,
 * comme l'ADM (décision de Valérie du 30/09/2026, suite de l'ouverture du « Détail des sessions »).
 * Lecture seulement : renvoie le compte à lire, ou null (compte propre).
 */
export function pilotReadAccountOverride(profileType: string, accountId: string | null): string | null {
  return profileType === "COO" && accountId ? accountId : null;
}
